// WebAuthn binary encoding helpers
export function bufferToBase64Url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let str = '';
    for (const byte of bytes) {
        str += String.fromCharCode(byte);
    }
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export function base64UrlToBuffer(base64url: string): ArrayBuffer {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/').replace(/=+$/, '');
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
    const binary = atob(padded);
    const buffer = new ArrayBuffer(binary.length);
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return buffer;
}

/**
 * Recursively converts base64url strings in a CredentialCreationOptions object
 * returned from the server to ArrayBuffers required by navigator.credentials.create.
 */
export function prepareCreationOptions(options: any): PublicKeyCredentialCreationOptions {
    return {
        ...options,
        challenge: base64UrlToBuffer(options.challenge),
        user: {
            ...options.user,
            id: base64UrlToBuffer(options.user.id),
        },
        excludeCredentials: (options.excludeCredentials ?? []).map((c: any) => ({
            ...c,
            id: base64UrlToBuffer(c.id),
        })),
    };
}

/**
 * Converts a PublicKeyCredential from navigator.credentials.create into a
 * JSON-serialisable object to POST to the server.
 */
export function serializeAttestationCredential(credential: PublicKeyCredential): any {
    const response = credential.response as AuthenticatorAttestationResponse;
    return {
        id: credential.id,
        rawId: bufferToBase64Url(credential.rawId),
        type: credential.type,
        response: {
            attestationObject: bufferToBase64Url(response.attestationObject),
            clientDataJSON: bufferToBase64Url(response.clientDataJSON),
        },
    };
}

/**
 * Recursively converts base64url strings in an AssertionOptions object
 * returned from the server to ArrayBuffers required by navigator.credentials.get.
 */
export function prepareAssertionOptions(options: any): PublicKeyCredentialRequestOptions {
    return {
        ...options,
        challenge: base64UrlToBuffer(options.challenge),
        allowCredentials: (options.allowCredentials ?? []).map((c: any) => ({
            ...c,
            id: base64UrlToBuffer(c.id),
        })),
    };
}

/**
 * Converts a PublicKeyCredential from navigator.credentials.get into a
 * JSON-serialisable object to POST to the server.
 */
export function serializeAssertionCredential(credential: PublicKeyCredential): any {
    const response = credential.response as AuthenticatorAssertionResponse;
    return {
        id: credential.id,
        rawId: bufferToBase64Url(credential.rawId),
        type: credential.type,
        response: {
            authenticatorData: bufferToBase64Url(response.authenticatorData),
            clientDataJSON: bufferToBase64Url(response.clientDataJSON),
            signature: bufferToBase64Url(response.signature),
            userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null,
        },
    };
}
